import React, { useState, useEffect } from 'react';
import { Modal, Form, Button, Row, Col, InputGroup } from 'react-bootstrap';
import { oneKlicks } from '../../../SDK/oneclick';
import { OneKlicksFileSDK } from '../../../SDK/OneKlicksFileSDK';
import { toast } from 'react-toastify';
import { getuserdata } from '../../../services/apiService';

const PaymentModal = ({ show, setShow, invoiceData, onPaymentSuccess }) => {
    const [formData, setFormData] = useState({
        amount: '',
        paymentSource: '',
        referenceNumber: '',
        receipt: null,
        notes: '',
        date: new Date().toISOString().split('T')[0]
    });
    const [loading, setLoading] = useState(false);
    const [errors, setErrors] = useState({});
    const [selectedFile, setSelectedFile] = useState(null);

    useEffect(() => {
        if (show && invoiceData) {
            // Set maximum amount as the remaining balance
            const totalAmount = parseFloat(invoiceData.total_amount || 0);
            const paidAmount = parseFloat(invoiceData.paid_amount || 0);
            const remainingAmount = totalAmount - paidAmount;
            setFormData(prev => ({
                ...prev,
                amount: remainingAmount.toString()
            }));
        }
    }, [show, invoiceData]);

    const validateForm = () => {
        const newErrors = {};
        const amount = parseFloat(formData.amount);
        const totalAmount = parseFloat(invoiceData.total_amount || 0);
        const paidAmount = parseFloat(invoiceData.paid_amount || 0);
        const remainingAmount = totalAmount - paidAmount;

        if (!formData.amount || isNaN(amount) || amount <= 0) {
            newErrors.amount = 'Please enter a valid amount';
        } else if (amount > remainingAmount) {
            newErrors.amount = `Amount cannot exceed remaining balance of ₹${remainingAmount}`;
        }

        if (!formData.paymentSource) {
            newErrors.paymentSource = 'Please select payment source';
        }

        if (formData.paymentSource === 'upi' || formData.paymentSource === 'bank' || formData.paymentSource === 'card') {
            if (!formData.referenceNumber) {
                newErrors.referenceNumber = 'Reference number is required';
            }
            if (!selectedFile) {
                newErrors.receipt = 'Receipt upload is required';
            }
        }

        if (!formData.date) {
            newErrors.date = 'Payment date is required';
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleFileChange = (e) => {
        const file = e.target.files[0];
        setSelectedFile(file);
        setFormData(prev => ({
            ...prev,
            receipt: file
        }));
        
        if (errors.receipt) {
            setErrors(prev => ({
                ...prev,
                receipt: ''
            }));
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        if (!validateForm()) {
            return;
        }

        setLoading(true);
        
        try {
            let receiptFileId = null;
            
            // Upload receipt if provided
            if (selectedFile) {
                const uploadResult = await OneKlicksFileSDK.uploadFile('payments', 'public', selectedFile);
                if (uploadResult && uploadResult.fileId) {
                    receiptFileId = uploadResult.fileId;
                }
            }

            const userid = await getuserdata('id');
            const timestamp = new Date().getTime();
            
            // Create payment record
            const paymentData = {
                invoice_id: invoiceData.id,
                amount: parseFloat(formData.amount),
                payment_source: formData.paymentSource,
                reference_number: formData.referenceNumber || '',
                receipt_file_id: receiptFileId || '',
                payment_date: formData.date,
                notes: formData.notes || '',
                status: 'completed',
                created_by: userid,
                transaction_id: timestamp
            };

            const tableSchema = '';
            const tableName = 'invoice_payments';
            const conditions = {};
            
            const paymentResponse = await oneKlicks.create(tableSchema, tableName, paymentData, conditions);
            
            if (paymentResponse.success) {
                // Update invoice with new paid amount - fix string concatenation issue
                const currentPaidAmount = parseFloat(invoiceData.paid_amount || 0);
                const paymentAmount = parseFloat(formData.amount);
                const newPaidAmount = currentPaidAmount + paymentAmount;
                const newStatus = newPaidAmount >= invoiceData.total_amount ? 'paid' : 'partial_payment';
                
                const invoiceUpdateData = {
                    paid_amount: newPaidAmount,
                    status: newStatus
                };
                
                const invoiceConditions = { id: invoiceData.id };
                const invoiceUpdateResponse = await oneKlicks.update(tableSchema, 'invoices', invoiceUpdateData, invoiceConditions);
                
                if (invoiceUpdateResponse.success) {
                    // Create income record for the payment
                    const incomeData = {
                        add_by: userid,
                        transaction_id: timestamp,
                        incomeType: 'invoice_payment',
                        clientYes: invoiceData.client,
                        amount: formData.amount,
                        date: formData.date,
                        description: `Payment for Invoice #${invoiceData.id} - ${formData.paymentSource.toUpperCase()}`,
                        status: 'active',
                        notes: formData.notes || `Payment received for invoice ${invoiceData.id}`,
                        attachments: receiptFileId || '',
                        transactionType: formData.paymentSource,
                        referenceNumber: formData.referenceNumber || '',
                        toSendAccount: userid
                    };
                    
                    const incomeResponse = await oneKlicks.create(tableSchema, 'income', incomeData, {});
                    
                    if (incomeResponse.success) {
                        // Update balance
                        const balanceConditions = { account_id: userid };
                        const balanceResponse = await oneKlicks.read(tableSchema, 'balance', balanceConditions, 'id DESC', {}, {}, []);
                        
                        if (balanceResponse.success && balanceResponse.data.length > 0) {
                            const currentBalance = parseFloat(balanceResponse.data[0].amount);
                            const newBalance = currentBalance + parseFloat(formData.amount);
                            
                            const balanceUpdateData = { amount: newBalance };
                            const balanceUpdateConditions = { id: balanceResponse.data[0].id };
                            await oneKlicks.update(tableSchema, 'balance', balanceUpdateData, balanceUpdateConditions);
                            
                            // Add balance sheet entry
                            const balanceSheetData = {
                                account_id: userid,
                                transaction_id: timestamp,
                                credit: parseFloat(formData.amount),
                                debit: 0,
                                balance: newBalance,
                                date: formData.date,
                                description: `Payment received for invoice ${invoiceData.id}`,
                                status: 'active'
                            };
                            await oneKlicks.create(tableSchema, 'balance_sheet', balanceSheetData, {});
                        }
                        
                        toast.success('Payment recorded successfully!');
                        onPaymentSuccess();
                        handleClose();
                    } else {
                        toast.error('Failed to create income record');
                    }
                } else {
                    toast.error('Failed to update invoice status');
                }
            } else {
                toast.error('Failed to record payment');
            }
        } catch (error) {
            console.error('Error processing payment:', error);
            toast.error('Something went wrong while processing payment');
        } finally {
            setLoading(false);
        }
    };

    const handleClose = () => {
        setFormData({
            amount: '',
            paymentSource: '',
            referenceNumber: '',
            receipt: null,
            notes: '',
            date: new Date().toISOString().split('T')[0]
        });
        setSelectedFile(null);
        setErrors({});
        setShow(false);
    };

    const totalAmount = invoiceData ? parseFloat(invoiceData.total_amount || 0) : 0;
    const paidAmount = invoiceData ? parseFloat(invoiceData.paid_amount || 0) : 0;
    const remainingAmount = totalAmount - paidAmount;

    return (
        <Modal show={show} onHide={handleClose} size="lg">
            <Modal.Header closeButton>
                <Modal.Title>Record Payment</Modal.Title>
            </Modal.Header>
            <Form onSubmit={handleSubmit}>
                <Modal.Body>
                    {invoiceData && (
                        <div className="mb-3 p-3 bg-light rounded">
                            <h6>Invoice Details</h6>
                            <p className="mb-1"><strong>Invoice ID:</strong> #{invoiceData.id}</p>
                            <p className="mb-1"><strong>Total Amount:</strong> ₹{invoiceData.total_amount}</p>
                            <p className="mb-1"><strong>Paid Amount:</strong> ₹{invoiceData.paid_amount || 0}</p>
                            <p className="mb-0"><strong>Remaining Balance:</strong> ₹{remainingAmount}</p>
                        </div>
                    )}
                    
                    <Row>
                        <Col md={6}>
                            <Form.Group className="mb-3">
                                <Form.Label>Payment Amount *</Form.Label>
                                <Form.Control
                                    type="number"
                                    step="0.01"
                                    min="0.01"
                                    max={remainingAmount}
                                    value={formData.amount}
                                    onChange={(e) => {
                                        setFormData({ ...formData, amount: e.target.value });
                                        if (errors.amount) {
                                            setErrors({ ...errors, amount: '' });
                                        }
                                    }}
                                    isInvalid={!!errors.amount}
                                    required
                                />
                                <Form.Control.Feedback type="invalid">
                                    {errors.amount}
                                </Form.Control.Feedback>
                            </Form.Group>
                        </Col>
                        <Col md={6}>
                            <Form.Group className="mb-3">
                                <Form.Label>Payment Date *</Form.Label>
                                <Form.Control
                                    type="date"
                                    value={formData.date}
                                    onChange={(e) => {
                                        setFormData({ ...formData, date: e.target.value });
                                        if (errors.date) {
                                            setErrors({ ...errors, date: '' });
                                        }
                                    }}
                                    isInvalid={!!errors.date}
                                    required
                                />
                                <Form.Control.Feedback type="invalid">
                                    {errors.date}
                                </Form.Control.Feedback>
                            </Form.Group>
                        </Col>
                    </Row>

                    <Row>
                        <Col md={12}>
                            <Form.Group className="mb-3">
                                <Form.Label>Payment Source *</Form.Label>
                                <Form.Select
                                    value={formData.paymentSource}
                                    onChange={(e) => {
                                        setFormData({ ...formData, paymentSource: e.target.value });
                                        if (errors.paymentSource) {
                                            setErrors({ ...errors, paymentSource: '' });
                                        }
                                    }}
                                    isInvalid={!!errors.paymentSource}
                                    required
                                >
                                    <option value="">Select Payment Source</option>
                                    <option value="cash">Cash</option>
                                    <option value="upi">UPI</option>
                                    <option value="bank">Bank Transfer</option>
                                    <option value="card">Card Payment</option>
                                </Form.Select>
                                <Form.Control.Feedback type="invalid">
                                    {errors.paymentSource}
                                </Form.Control.Feedback>
                            </Form.Group>
                        </Col>
                    </Row>

                    {(formData.paymentSource === 'upi' || formData.paymentSource === 'bank' || formData.paymentSource === 'card') && (
                        <Row>
                            <Col md={6}>
                                <Form.Group className="mb-3">
                                    <Form.Label>Reference Number *</Form.Label>
                                    <Form.Control
                                        type="text"
                                        placeholder="Enter reference/transaction ID"
                                        value={formData.referenceNumber}
                                        onChange={(e) => {
                                            setFormData({ ...formData, referenceNumber: e.target.value });
                                            if (errors.referenceNumber) {
                                                setErrors({ ...errors, referenceNumber: '' });
                                            }
                                        }}
                                        isInvalid={!!errors.referenceNumber}
                                        required
                                    />
                                    <Form.Control.Feedback type="invalid">
                                        {errors.referenceNumber}
                                    </Form.Control.Feedback>
                                </Form.Group>
                            </Col>
                            <Col md={6}>
                                <Form.Group className="mb-3">
                                    <Form.Label>Upload Receipt *</Form.Label>
                                    <Form.Control
                                        type="file"
                                        accept="image/*,.pdf"
                                        onChange={handleFileChange}
                                        isInvalid={!!errors.receipt}
                                        required
                                    />
                                    <Form.Control.Feedback type="invalid">
                                        {errors.receipt}
                                    </Form.Control.Feedback>
                                    {selectedFile && (
                                        <small className="text-muted">
                                            Selected: {selectedFile.name}
                                        </small>
                                    )}
                                </Form.Group>
                            </Col>
                        </Row>
                    )}

                    <Row>
                        <Col md={12}>
                            <Form.Group className="mb-3">
                                <Form.Label>Notes</Form.Label>
                                <Form.Control
                                    as="textarea"
                                    rows={3}
                                    placeholder="Enter any additional notes about this payment"
                                    value={formData.notes}
                                    onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                                />
                            </Form.Group>
                        </Col>
                    </Row>
                </Modal.Body>

                <Modal.Footer>
                    <Button variant="secondary" onClick={handleClose}>
                        Cancel
                    </Button>
                    <Button variant="primary" type="submit" disabled={loading}>
                        {loading ? 'Processing...' : 'Record Payment'}
                    </Button>
                </Modal.Footer>
            </Form>
        </Modal>
    );
};

export default PaymentModal;
